/**
 * ActiveViewers - Display component for showing users currently viewing a matrix
 * 
 * Shows a stack of user avatars with initials, similar to Microsoft's co-authoring
 * indicators in Office apps. Includes tooltip with full user names.
 * 
 * Features:
 * - Stacked avatar display (max 5 visible, +N for overflow)
 * - Hover tooltip showing all viewer names
 * - Gray / Away avatars for background tabs
 * - Connection status indicator
 * - Animated entry/exit of avatars
 */

import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { createPortal } from 'react-dom';

// Color palette for avatar backgrounds (consistent per user)
const AVATAR_COLORS = [
  '#4285F4', // Google Blue
  '#EA4335', // Google Red
  '#34A853', // Google Green
  '#FBBC05', // Google Yellow
  '#9C27B0', // Purple
  '#00BCD4', // Cyan
  '#FF5722', // Deep Orange
  '#607D8B', // Blue Grey
  '#E91E63', // Pink
  '#3F51B5', // Indigo
];

/**
 * Get consistent color for a user based on their ID
 */
const getAvatarColor = (userId) => {
  if (!userId) return AVATAR_COLORS[0];
  
  // Simple hash function to get consistent color
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

/**
 * Single avatar component
 */
const Avatar = memo(({ user, index, size = 32, showBorder = true }) => {
  const isAway = user.status === 'away';
  const backgroundColor = isAway ? '#9CA3AF' : getAvatarColor(user.id);
  
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.45,
        fontWeight: 600,
        border: showBorder ? '2px solid white' : 'none',
        boxShadow: showBorder ? '0 1px 3px rgba(0, 0, 0, 0.2)' : 'none',
        marginLeft: index > 0 ? (showBorder ? -8 : -4) : 0,
        cursor: 'default',
        flexShrink: 0,
        opacity: isAway ? 0.75 : 1,
        filter: isAway ? 'grayscale(0.35)' : 'none',
      }}
      title={
        isAway
          ? `${user.displayName || user.email} (away — tab in background)`
          : (user.displayName || user.email)
      }
    >
      {user.initials || '??'}
    </div>
  );
});

Avatar.displayName = 'Avatar';

/**
 * Overflow indicator (+N more)
 */
const OverflowIndicator = memo(({ count, size = 32 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: '#6B7280',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size * 0.4,
      fontWeight: 600,
      marginLeft: -4,
      cursor: 'default',
      flexShrink: 0,
    }}
    title={`+${count} more viewers`}
  >
    +{count}
  </div>
));

OverflowIndicator.displayName = 'OverflowIndicator';

/**
 * Connection status dot
 */
const ConnectionDot = memo(({ isConnected, connectionState }) => {
  let color = '#6B7280'; // Gray - disconnected
  let title = 'Disconnected';
  
  if (connectionState === 'connecting') {
    color = '#F59E0B'; // Amber - connecting
    title = 'Connecting...';
  } else if (isConnected) {
    color = '#10B981'; // Green - connected
    title = 'Connected';
  } else if (connectionState === 'error') {
    color = '#EF4444'; // Red - error
    title = 'Connection error';
  }
  
  return (
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: color,
        marginRight: 6,
        flexShrink: 0,
        animation: connectionState === 'connecting' ? 'pulse 1.5s infinite' : 'none',
      }}
      title={title}
    />
  );
});

ConnectionDot.displayName = 'ConnectionDot';

/**
 * Tooltip showing all viewers
 */
const ViewersTooltip = memo(({ viewers, isVisible, style = {} }) => {
  if (!isVisible || viewers.length === 0) return null;
  
  return (
    <div
      style={{
        position: 'fixed',
        backgroundColor: 'white',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '8px 0',
        minWidth: 200,
        maxWidth: 280,
        zIndex: 1000,
        border: '1px solid #E5E7EB',
        ...style,
      }}
    >
      <div
        style={{
          padding: '4px 12px 8px',
          borderBottom: '1px solid #E5E7EB',
          fontSize: 12,
          fontWeight: 600,
          color: '#6B7280',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Currently Viewing ({viewers.length})
      </div>
      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        {viewers.map((viewer, index) => (
          <div
            key={viewer.id || index}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              gap: 10,
            }}
          >
            <Avatar user={viewer} index={0} size={28} showBorder={false} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: viewer.status === 'away' ? '#6B7280' : '#111827',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {viewer.displayName || 'Unknown User'}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: '#6B7280',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {viewer.status === 'away' ? 'Away — tab in background' : viewer.email}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

ViewersTooltip.displayName = 'ViewersTooltip';

/**
 * Main ActiveViewers component
 */
const ActiveViewers = ({
  viewers = [],
  isConnected = false,
  connectionState = 'disconnected',
  maxVisible = 5,
  showConnectionStatus = true,
  compact = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const anchorRef = useRef(null);
  const [tooltipStyle, setTooltipStyle] = useState(null);

  const sortedViewers = useMemo(() => {
    return [...viewers].sort((a, b) => {
      const aAway = a.status === 'away' ? 1 : 0;
      const bAway = b.status === 'away' ? 1 : 0;
      if (aAway !== bAway) return aAway - bAway;
      return (a.joinedAt || 0) - (b.joinedAt || 0);
    });
  }, [viewers]);

  const visibleViewers = sortedViewers.slice(0, maxVisible);
  const overflowCount = Math.max(0, sortedViewers.length - maxVisible);
  const hasViewers = sortedViewers.length > 0;

  const estimateTooltipWidth = useMemo(() => {
    // Keep within ViewersTooltip min/max width range.
    // If we want to be smarter later, we could measure.
    return 240;
  }, []);

  const updateTooltipPosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const margin = 8;

    // Try to right-align the tooltip to the anchor.
    let left = rect.right - estimateTooltipWidth;
    if (left < margin) left = margin;
    if (left + estimateTooltipWidth > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - estimateTooltipWidth);
    }

    // Place below the anchor; if it would go off-screen, place above.
    const estimatedHeight = 260; // header + up to ~200px list
    let top = rect.bottom + margin;
    if (top + estimatedHeight > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - margin - estimatedHeight);
    }

    setTooltipStyle({
      top,
      left,
      // Ensure tooltip width stays within ViewersTooltip constraints
      width: estimateTooltipWidth,
    });
  }, [estimateTooltipWidth]);

  useEffect(() => {
    if (!showTooltip) return;

    // Keep tooltip pinned during scroll/resize.
    const handle = () => updateTooltipPosition();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [showTooltip, updateTooltipPosition]);

  // Don't render anything if not connected and no viewers
  // (must be AFTER hooks to satisfy rules-of-hooks)
  if (!isConnected && viewers.length === 0 && connectionState !== 'connecting') {
    return null;
  }
  
  return (
    <div
      ref={anchorRef}
      style={{
        padding: '0.35rem 1rem',
        backgroundColor: '#f3f4f6',
        color: '#374151',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '0.875rem',
        fontWeight: 'normal',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={() => {
        if (!hasViewers) return;
        updateTooltipPosition();
        setShowTooltip(true);
      }}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Connection status */}
      {showConnectionStatus && (
        <ConnectionDot isConnected={isConnected} connectionState={connectionState} />
      )}
      
      {/* Avatar stack */}
      {hasViewers && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {visibleViewers.map((viewer, index) => (
            <Avatar
              key={viewer.id || index}
              user={viewer}
              index={index}
              size={24}
              showBorder={false}
            />
          ))}
          
          {overflowCount > 0 && (
            <OverflowIndicator count={overflowCount} size={24} />
          )}
        </div>
      )}
      
      {/* Viewer count label */}
      {!compact && hasViewers && (
        <span
          style={{
            whiteSpace: 'nowrap',
          }}
        >
          {viewers.length} viewing
        </span>
      )}
      
      {/* Connecting state */}
      {!hasViewers && connectionState === 'connecting' && !compact && (
        <span
          style={{
            color: '#6B7280',
            fontStyle: 'italic',
          }}
        >
          Connecting...
        </span>
      )}
      
      {/* Tooltip rendered in a portal to avoid clipping by parent overflow containers */}
      {showTooltip && tooltipStyle
        ? createPortal(
            <ViewersTooltip viewers={sortedViewers} isVisible={showTooltip} style={tooltipStyle} />,
            document.body
          )
        : null}
      
      {/* CSS for pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );
};

export default memo(ActiveViewers);

